import { FC, useEffect, useRef, useState } from "react";
import { Button, Form, Input, message } from "antd";
import { useRequest, useThrottleFn } from "ahooks";

import styles from "../../index.less";

import { ResponseCode } from "../../../../../../constants";
import {
  ChangeType,
  DisplayPayStatusEnum,
  getUserPhoneAndEmailService,
  getVerCodeService,
  OrderInfo,
  PayType,
  requestPayService,
} from "../../../../../../service";

interface PayWithBalanceProps {
  /** 订单信息 */
  orderInfo?: OrderInfo;
  /** 实付金额 */
  payAmount?: number;
  /** 余额 */
  balance?: number;
  /** 支付成功回调 */
  onSuccess: (payId: string) => void;
}

/**
 * 余额支付
 * @returns
 */
const PayWithBalance: FC<PayWithBalanceProps> = ({
  orderInfo,
  payAmount,
  balance,
  onSuccess,
}) => {
  console.log("balance", balance);
  const [countDown, setCountDown] = useState(0);
  const timerId = useRef<NodeJS.Timeout>();
  const [hadSent, setHadSent] = useState(false);

  useEffect(() => {
    return () => {
      clearInterval(timerId.current);
    };
  }, []);

  const { run: getCode } = useThrottleFn(
    async () => {
      if (countDown > 0) return;
      setCountDown(60);
      setHadSent(true);
      timerId.current = setInterval(() => {
        setCountDown((v) => {
          if (v <= 1) {
            clearInterval(timerId.current);
            return 0;
          }
          return v - 1;
        });
      }, 1000);

      const rsp = await getVerCodeService({
        businessId: orderInfo!.orderId,
        changeType: ChangeType.CONSUME,
      });

      if (rsp.code !== ResponseCode.Success) {
        message.error(rsp.msg);
        return;
      }
    },
    { wait: 500 }
  );

  const { run: handleSubmit } = useRequest(
    async (values: { verCode: string }) => {
      const rsp = await requestPayService({
        verCode: values.verCode,
        orderId: orderInfo?.orderId,
        payType: PayType.BALANCE,
      });

      if (rsp.code !== ResponseCode.Success) {
        message.error(rsp.msg);
        return;
      }

      if (rsp.data.payStatus === DisplayPayStatusEnum.DONE) {
        onSuccess(rsp.data.payId);
        return;
      }

      message.error(rsp.msg || "支付失败");
    },
    { manual: true, throttleWait: 500 }
  );

  const { data: phoneOrEmail } = useRequest(async () => {
    const rsp = await getUserPhoneAndEmailService();
    if (rsp.code !== ResponseCode.Success) {
      message.error(rsp.msg);
    }
    return rsp.data;
  });

  return (
    <div className={styles["with-balance"]}>
      <div className={styles.title}>请输入验证码完成支付</div>
      <div className={styles.description}>
        {phoneOrEmail?.phone ? (
          <div>
            <span>验证码已发送至手机号</span>
            <span className={styles.highlight}>{phoneOrEmail?.phone}</span>
          </div>
        ) : (
          <div>
            <span>验证码已发送至邮箱</span>
            <span className={styles.highlight}>{phoneOrEmail?.email}</span>
          </div>
        )}
      </div>

      <Form className={styles.form} onFinish={handleSubmit}>
        <Form.Item name="verCode" noStyle rules={[{ required: true }]}>
          <Input
            suffix={
              <Button type="link" onClick={getCode} size="small">
                {countDown > 0
                  ? `${countDown}s`
                  : hadSent
                  ? "重新获取"
                  : "获取验证码"}
              </Button>
            }
          />
        </Form.Item>

        <Form.Item noStyle shouldUpdate>
          {({ getFieldsValue }) => {
            const { verCode } = getFieldsValue();
            return (
              <Button
                type="primary"
                className={styles.confirm}
                htmlType="submit"
                disabled={!payAmount || payAmount <= 0 || !verCode}
              >
                确认支付&nbsp;{payAmount ? `¥${payAmount}` : "--"}
              </Button>
            );
          }}
        </Form.Item>
      </Form>
    </div>
  );
};

export default PayWithBalance;
